import { useCallback, useState } from 'react'

/**
 * Retry session data for SCA (Strong Customer Authentication) payment flows.
 * When a Stripe payment requires 3D Secure authentication and fails, we save
 * this data to enable retry without re-creating the PaymentIntent.
 */
export interface ScaRetrySession<TPlanId = string, TInterval = string> {
  /** Stripe client secret for the PaymentIntent */
  clientSecret: string
  /** Payment method ID to use for retry (optional, but helps avoid "missing payment method" errors) */
  paymentMethodId: string | null
  /** Plan ID that was being updated to */
  planId: TPlanId
  /** Billing interval that was being updated to */
  interval: TInterval
  /** Quantity/channel count that was being updated to */
  quantity: number
}

/**
 * Retry state for SCA payment flows.
 * Follows React best practices by using a single state object for related values.
 */
export interface ScaRetryState<TPlanId = string, TInterval = string> {
  /** Whether user is currently in retry mode (payment failed/canceled, can retry) */
  isRetrying: boolean
  /** Saved retry session data (null when no retry available) */
  session: ScaRetrySession<TPlanId, TInterval> | null
}

/**
 * Parameters for checking if retry session is still valid.
 * If any of these changed since the session was saved, we must create a new PaymentIntent.
 */
export interface ScaRetryParameters<TPlanId = string, TInterval = string> {
  planId: TPlanId
  interval: TInterval
  quantity: number
}

/**
 * Custom hook for managing SCA (Strong Customer Authentication) retry state.
 *
 * Centralizes the retry logic for Stripe 3D Secure payment flows. When a payment
 * requires authentication and fails, this hook tracks the session data needed to
 * retry without creating a new Stripe PaymentIntent.
 *
 * **Why a single object state?**
 * React recommends using a single state object when values always change together,
 * which is the case for retry session data. This reduces unnecessary re-renders
 * and makes the state updates atomic.
 *
 * @template TPlanId - Type for plan identifier (e.g., ObPlanId)
 * @template TInterval - Type for billing interval (e.g., BillingInterval, AppShellBillingInterval)
 *
 * @returns Object with retry state and helper functions
 *
 * @example
 * ```tsx
 * const { retryState, saveRetryData, clearRetryData, hasParametersChanged, canRetry } =
 *   useScaRetryState<ObPlanId, BillingInterval>()
 *
 * // After payment fails:
 * saveRetryData({
 *   clientSecret: 'pi_xxx_secret_yyy',
 *   paymentMethodId: 'pm_xxx',
 *   planId: 'essentials',
 *   interval: 'year',
 *   quantity: 10,
 * })
 *
 * // Before retrying:
 * if (canRetry() && !hasParametersChanged({ planId, interval, quantity })) {
 *   // SCENARIO A: Reuse saved client secret
 * } else {
 *   // SCENARIO B: Create new PaymentIntent
 * }
 * ```
 */
export function useScaRetryState<TPlanId = string, TInterval = string>() {
  const [retryState, setRetryState] = useState<
    ScaRetryState<TPlanId, TInterval>
  >({
    isRetrying: false,
    session: null,
  })

  /**
   * Saves retry session data and enables retry mode.
   * Call this when a Stripe 3DS payment fails or is canceled.
   */
  const saveRetryData = useCallback(
    (session: ScaRetrySession<TPlanId, TInterval>) => {
      setRetryState({
        isRetrying: true,
        session,
      })
    },
    [],
  )

  /**
   * Clears all retry state.
   * Call this after successful payment or when starting a new update with different parameters.
   */
  const clearRetryData = useCallback(() => {
    setRetryState({
      isRetrying: false,
      session: null,
    })
  }, [])

  /**
   * Checks if the current parameters differ from the saved retry session.
   * If parameters changed, we must create a new Stripe PaymentIntent.
   *
   * @returns true if parameters changed (must create new PaymentIntent), false if same (can retry)
   */
  const hasParametersChanged = useCallback(
    (currentParams: ScaRetryParameters<TPlanId, TInterval>): boolean => {
      if (!retryState.session) return true

      return (
        retryState.session.planId !== currentParams.planId ||
        retryState.session.interval !== currentParams.interval ||
        retryState.session.quantity !== currentParams.quantity
      )
    },
    [retryState.session],
  )

  /**
   * Checks if retry is available (has saved session and user is in retry mode).
   * Use this to determine if SCENARIO A (pure retry) is possible.
   *
   * @returns true if retry is available with saved client secret
   */
  const canRetry = useCallback((): boolean => {
    return retryState.isRetrying && retryState.session !== null
  }, [retryState])

  /**
   * Enables retry mode without changing the session data.
   * Use this when payment fails but you already have session data saved.
   */
  const enableRetryMode = useCallback(() => {
    setRetryState((prev) => ({
      ...prev,
      isRetrying: true,
    }))
  }, [])

  return {
    retryState,
    saveRetryData,
    clearRetryData,
    hasParametersChanged,
    canRetry,
    enableRetryMode,
  }
}
